import { Check, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { EVENT_LOCATIONS, OFFER_MARKUP } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { useBackClose } from "../hooks/use-back-close";

export const EventDetailDialog = ({ service, onClose, onBook }) => {
  const { t, L } = useLanguage();
  const open = !!service;
  useBackClose(open, onClose);

  if (!service) return null;

  const closeViaBack = () => window.history.back();
  const hasPackages = !!service.packages?.length;

  const bookPkg = (p) => {
    onBook({
      service: service.name,
      category: "Surprise Event Management",
      items: [{ label: p.label, labelBn: p.labelBn, price: p.price, qty: 1 }],
      total: p.price,
      locationOptions: EVENT_LOCATIONS,
    });
    onClose();
  };

  const book = () => {
    onBook({
      service: service.name,
      category: "Surprise Event Management",
      locationOptions: EVENT_LOCATIONS,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeViaBack()}>
      <DialogContent
        className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto"
        data-testid="event-detail-dialog"
      >
        <div className="rounded-2xl overflow-hidden h-44 sm:h-52 -mx-2 -mt-2 mb-2">
          <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
        </div>
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">{L(service, "name")}</DialogTitle>
          <DialogDescription>{L(service, "desc")}</DialogDescription>
        </DialogHeader>

        {service.includes?.length > 0 && (
          <div className="mt-1">
            <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase mb-2">
              {t.includes}
            </p>
            <ul className="space-y-1.5">
              {(L(service, "includes") === service.includes ? service.includes : service.includesBn).map(
                (inc) => (
                  <li key={inc} className="flex items-start gap-2 text-base text-stone-600">
                    <Check className="w-4 h-4 text-[#4A7C59] shrink-0 mt-0.5" />
                    {inc}
                  </li>
                )
              )}
            </ul>
          </div>
        )}

        {hasPackages && (
          <div className="mt-4">
            <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase mb-2">
              {t.choosePackage}
            </p>
            <div className="space-y-2">
              {service.packages.map((p, i) => (
                <div
                  key={p.label}
                  data-testid={`package-option-${i}`}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-[#FAF9F6] px-4 py-3"
                >
                  <span className="text-base font-medium text-stone-800 min-w-0 leading-snug">
                    {L(p, "label")}
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-right">
                      <span className="block text-sm line-through text-stone-400">
                        ₹{(p.price + OFFER_MARKUP.events).toLocaleString("en-IN")}
                      </span>
                      <span className="block text-base font-bold text-[#EAB308]">
                        ₹{p.price.toLocaleString("en-IN")}
                      </span>
                    </span>
                    <button
                      onClick={() => bookPkg(p)}
                      data-testid={`package-book-${i}`}
                      className="rounded-full bg-[#E85D75] text-white text-sm font-semibold px-4 py-2 hover:bg-[#d64a63] transition-colors duration-200 whitespace-nowrap"
                    >
                      Book Service
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {service.note && (
          <p className="mt-3 text-xs text-stone-500 italic">{L(service, "note")}</p>
        )}

        <p className="mt-2 flex items-start gap-1.5 text-xs text-stone-500">
          <MapPin className="w-3.5 h-3.5 text-[#4A7C59] shrink-0 mt-0.5" />
          {t.booking.riverside}
        </p>

        {!hasPackages && (
          <button
            onClick={book}
            data-testid="event-detail-book-button"
            className="mt-4 w-full rounded-full bg-[#E85D75] text-white font-semibold py-3 hover:bg-[#d64a63] transition-colors duration-200"
          >
            Book Service
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
